import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { BsList, BsPerson, BsX } from 'react-icons/bs';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Logo from '../../assets/logoLaCanaCouleur.png';
import { CartContext } from '../../contexts/cart.context';
import { UserContext } from '../../contexts/user.context';
import { signOutUser } from '../../utils/firebase/firebase.utils';
import CartDropdown from '../cart-dropdown/cart-dropdown.component';
import CartIcon from '../cart-icon/cart-icon.component';
import navLinks from './utils';
import './navigation.styles.scss';

// Au-delà de ce défilement la barre se compacte ; elle ne se masque qu'ensuite,
// pour ne pas disparaître au moindre mouvement en haut de page.
const COMPACT_FROM = 24;
const HIDE_FROM = 150;

const Navigation = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isCartOpen } = useContext(CartContext);
  const { currentUser } = useContext(UserContext);

  const [openPanel, setOpenPanel] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  const [isHidden, setIsHidden] = useState(false);

  const closePanelTimer = useRef(null);

  const closeAll = useCallback(() => {
    setOpenPanel(null);
    setIsDrawerOpen(false);
  }, []);

  const navigateAndScrollTop = useCallback(
    (link) => {
      navigate(link);
      window.scrollTo(0, 0);
      closeAll();
    },
    [navigate, closeAll]
  );

  // Un lien du méga-menu ou du tiroir doit refermer ce qu'il vient d'ouvrir.
  const handleLinkClick = useCallback(() => {
    window.scrollTo(0, 0);
    closeAll();
  }, [closeAll]);

  // Le survol ouvre le panneau ; la fermeture est différée pour laisser traverser
  // l'espace entre l'entrée de menu et le panneau sans le voir se refermer.
  const openPanelNow = useCallback((label) => {
    clearTimeout(closePanelTimer.current);
    setOpenPanel(label);
  }, []);

  const closePanelSoon = useCallback(() => {
    clearTimeout(closePanelTimer.current);
    closePanelTimer.current = setTimeout(() => setOpenPanel(null), 120);
  }, []);

  useEffect(() => () => clearTimeout(closePanelTimer.current), []);

  // Changement de page : on ne laisse jamais un menu ouvert derrière soi.
  // `pathname` n'est pas lu dans le corps de l'effet, c'est son déclencheur :
  // `closeAll` étant mémoïsé sur [], le retirer figerait l'effet au premier rendu
  // et les menus resteraient ouverts d'une page à l'autre.
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname est la dépendance déclenchante, pas une valeur lue
  useEffect(() => {
    closeAll();
  }, [pathname, closeAll]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeAll();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeAll]);

  // Tiroir mobile ouvert : la page derrière ne doit pas défiler.
  useEffect(() => {
    document.body.style.overflow = isDrawerOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isDrawerOpen]);

  useEffect(() => {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const update = () => {
      const currentY = window.scrollY;
      setIsCompact(currentY > COMPACT_FROM);
      setIsHidden(currentY > HIDE_FROM && currentY > lastScrollY);
      lastScrollY = currentY;
      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const headerClassName = [
    'navigation',
    isCompact ? 'is-compact' : '',
    // Un menu ouvert doit rester atteignable, même si la page défile sous la souris.
    isHidden && !openPanel && !isDrawerOpen ? 'is-hidden' : '',
    openPanel ? 'has-panel' : ''
  ]
    .filter(Boolean)
    .join(' ');

  const activeLink = navLinks.find((link) => link.label === openPanel);

  // /shop/hasch correspond à la fois à BOUTIQUE et à HASCH : on ne souligne que
  // l'entrée la plus précise, sinon deux liens s'affichent comme actifs.
  const currentPath = pathname.toLowerCase();
  const currentValue = navLinks
    .map((link) => link.value.toLowerCase())
    .filter(
      (value) => currentPath === value || currentPath.startsWith(`${value}/`)
    )
    .sort((a, b) => b.length - a.length)[0];

  return (
    // Le survol ne fait que devancer la fermeture des panneaux : Échap la déclenche
    // déjà pour tout le monde (voir l'effet `handleKeyDown` plus haut).
    // biome-ignore lint/a11y/noStaticElementInteractions: confort à la souris, équivalent clavier déjà fourni par Échap
    <header className={headerClassName} onMouseLeave={closePanelSoon}>
      <div className="nav-bar">
        <button
          type="button"
          className="nav-burger"
          aria-label="Ouvrir le menu"
          aria-expanded={isDrawerOpen}
          onClick={() => setIsDrawerOpen(true)}
        >
          <BsList size={26} />
        </button>

        <Link className="nav-logo" to="/" onClick={handleLinkClick}>
          <img src={Logo} alt="Lacana CBD Shop" />
        </Link>

        <nav className="nav-main" aria-label="Navigation principale">
          <ul>
            {navLinks.map((link) => (
              <li
                key={link.label}
                className={openPanel === link.label ? 'is-open' : ''}
                onMouseEnter={() =>
                  link.panel ? openPanelNow(link.label) : closePanelSoon()
                }
              >
                <Link
                  to={link.value}
                  className={
                    link.value.toLowerCase() === currentValue
                      ? 'is-current'
                      : ''
                  }
                  aria-haspopup={link.panel ? 'true' : undefined}
                  aria-expanded={
                    link.panel ? openPanel === link.label : undefined
                  }
                  onFocus={() =>
                    link.panel ? openPanelNow(link.label) : setOpenPanel(null)
                  }
                  onClick={handleLinkClick}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="nav-actions">
          {currentUser ? (
            <button type="button" className="nav-action" onClick={signOutUser}>
              DÉCONNEXION
            </button>
          ) : (
            <Link className="nav-action" to="/auth" onClick={handleLinkClick}>
              CONNEXION
            </Link>
          )}
          <CartIcon />
        </div>

        {isCartOpen && <CartDropdown />}
      </div>

      {activeLink?.panel && (
        // Le survol maintient le panneau ouvert pendant qu'on le traverse à la
        // souris ; au clavier, la navigation par tabulation le garde ouvert d'elle-même.
        // biome-ignore lint/a11y/noStaticElementInteractions: confort à la souris, sans équivalent nécessaire au clavier
        <div
          className="mega-menu"
          onMouseEnter={() => openPanelNow(activeLink.label)}
        >
          <div className="mega-menu-inner">
            <div>
              <p className="mega-menu-title">{activeLink.panel.title}</p>
              <p className="mega-menu-text">{activeLink.panel.text}</p>
              <ul className="mega-menu-links">
                {activeLink.panel.links.map((link) => (
                  <li key={link.value}>
                    <Link to={link.value} onClick={handleLinkClick}>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mega-menu-cards">
              {activeLink.panel.cards.map((card) => (
                <Link
                  key={card.value}
                  to={card.value}
                  className="mega-menu-card"
                  onClick={handleLinkClick}
                >
                  <span
                    className="mega-menu-card-image"
                    style={{ backgroundImage: `url(${card.image})` }}
                  />
                  <span className="mega-menu-card-label">{card.label}</span>
                  <span className="mega-menu-card-caption">{card.caption}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Voile de fermeture du tiroir : purement décoratif, d'où `presentation`.
          La fermeture au clavier passe par Échap, gérée plus haut. */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: capteur de clic, équivalent clavier fourni par Échap */}
      <div
        className={isDrawerOpen ? 'nav-overlay is-visible' : 'nav-overlay'}
        onClick={() => setIsDrawerOpen(false)}
        role="presentation"
      />

      <aside
        className={isDrawerOpen ? 'nav-drawer is-open' : 'nav-drawer'}
        aria-hidden={!isDrawerOpen}
      >
        <div className="nav-drawer-head">
          <img src={Logo} alt="" />
          <button
            type="button"
            className="nav-drawer-close"
            aria-label="Fermer le menu"
            onClick={() => setIsDrawerOpen(false)}
          >
            <BsX size={34} />
          </button>
        </div>

        <nav aria-label="Navigation mobile">
          <ul className="nav-drawer-links">
            {navLinks.map((link) => (
              <li key={link.label}>
                <button
                  type="button"
                  onClick={() => navigateAndScrollTop(link.value)}
                >
                  {link.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="nav-drawer-foot">
          {currentUser ? (
            <button type="button" onClick={signOutUser}>
              DÉCONNEXION
            </button>
          ) : (
            <button type="button" onClick={() => navigateAndScrollTop('/auth')}>
              <BsPerson size={20} />
              CONNEXION
            </button>
          )}
        </div>
      </aside>
    </header>
  );
};

export default Navigation;
